import { lazy, Suspense } from "react";
import { Provider } from "react-redux";
import { Route, Routes } from "react-router-dom";

import { AuthProvider } from "@/auth/AuthProvider";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";
import { isElectron } from "@/lib/runtimeEnv";
import { store } from "@/store";
import { DesktopShellBridge } from "@/desktop/DesktopShellBridge";
import { StartScreen } from "@/desktop/StartScreen";
import { DeckLanding } from "@/pages/DeckLanding";
import { DeckRoute } from "@/pages/DeckRoute";
import { DocumentsHome } from "@/pages/DocumentsHome";
import { NotFound } from "@/pages/NotFound";

// §8.6 The Excalidraw spike route — THROWAWAY. Lazy so the main bundle never carries
// Excalidraw for it (§10.3), and so deleting it is one import and one <Route>.
const DiagramSpike = lazy(() => import("@/pages/DiagramSpike"));

export function App() {
  return (
    <ErrorBoundary>
      <Provider store={store}>
        {/* Auth session (§9.1) sits above the routes so the whole studio — the export gate,
            the premium/library affordances — reacts to sign-in/out without a reload. It is
            independent of deck hydration: editing works signed-out. */}
        <AuthProvider>
          {/* §12.1 Which deck is open is a property of the URL, not of the store — which is
              what lets several pages hold DIFFERENT decks at once, and what a second desktop
              window will load (§14.7.2). Hydration therefore moved *inside* `/d/:deckId`
              (see `DeckRoute`); it is no longer a one-shot wrapper around the whole app. */}
          <Routes>
            {/* §14.7.3 `/` means different things in the two runtimes, and deliberately so: a
                browser tab that opens on a chooser feels broken, while a desktop app that
                mints an empty deck on every launch litters the user's own document list. */}
            <Route
              path="/"
              element={isElectron() ? <StartScreen /> : <DeckLanding />}
            />
            <Route path="/documents" element={<DocumentsHome />} />
            <Route path="/d/:deckId" element={<DeckRoute />} />
            {/* §8.6 throwaway — delete with `src/pages/DiagramSpike.tsx` once the four
                answers are recorded in `diagrams.todo.md`. */}
            <Route
              path="/spike/diagram"
              element={
                <Suspense fallback={<div className="p-6">Loading…</div>}>
                  <DiagramSpike />
                </Suspense>
              }
            />
            <Route path="*" element={<NotFound />} />
          </Routes>
          {/* §14.7 The desktop shell's renderer end: restores the open tabs on launch and
              turns native menu commands into the same actions the in-app buttons run. Renders
              nothing, and is never mounted on the website. */}
          {isElectron() && <DesktopShellBridge />}
          {/* Toasts: sign-in errors (§9.2.2) and library feedback (§8.4.4). Back at the
              default bottom-right offset: the AI assistant is a right-hand task pane now
              (see `DeckEditor`), not a bottom bar that toasts had to be lifted clear of. */}
          <Toaster />
        </AuthProvider>
      </Provider>
    </ErrorBoundary>
  );
}
